import AiBadge from '../AiBadge';
import { IconMindMapping, IconRobot, IconSettings, IconSound, IconVoice } from '@arco-design/web-react/icon';
import { PRIMARY, CYAN, PURPLE, WARNING } from '../../theme/palette';
import type { AICallLog } from '../../api/cdr';
import { computeLatency, intentDisplay, isIntentLog } from './_aiCallLog';

/** 流水线阶段（AI 工作可见）。状态：done 完成 / current 进行中（发光）/ pending 待执行 / none 未启用 */
interface PipeStage {
  key: string;
  label: string;
  icon: React.ReactNode;
  color: string;
  status: 'done' | 'current' | 'pending' | 'none';
  dur: string;
  extra?: React.ReactNode;
}

/**
 * AI 处理流水线：从 ai_call_log 推导 asr→意图→llm→tts→vc 五段式，
 * 当前段呼吸发光 + 连接线流动 + 各段耗时（LLM/TTS 用真实延迟，其余用轮次/偏移量）。
 * vc 段只在历史通话的日志里出现（现行链路不产生该阶段），判据见下方 vcStage。
 * 「AI 在干活」的可视化证据（design-spec §5）。
 */
export default function AIPipeline({ logs }: { logs: AICallLog[] }) {
  const latency = computeLatency(logs);
  const offsetOf = (p: (s: string) => boolean): number => {
    let off = 0;
    for (const l of logs) {
      if (p(l.stage)) off = Number(l.offset_ms) || 0;
    }
    return off;
  };
  const has = (p: (s: string) => boolean) => logs.some((l) => p(l.stage));

  const asrCount = logs.filter((l) => l.stage === 'asr_sentence_end').length;
  const llmDone = has((s) => s === 'llm_return');
  const llmCur = has((s) => s === 'llm_start') && !llmDone;
  // TTS 阶段判据：新口径 tts_turn（整轮收尾）/tts_segment（分段进行）与旧口径 tts_end/tts_start 同档，
  // 新数据只有前两者、历史通话只有后两者，故两边取或
  const ttsDone = has((s) => s === 'tts_end' || s === 'tts_turn');
  const ttsCur = has((s) => s === 'tts_start' || s === 'tts_segment') && !ttsDone;
  const vcDone = has((s) => s === 'vc_end');
  const vcCur = has((s) => s === 'vc_start') && !vcDone;
  // 变声段只在历史通话的日志里出现（现行链路不产生 vc_* 阶段）：
  // 出现即当时真跑过变声（连接/进行中/降级直放），没出现就不渲染本段。
  const vcFallback = has((s) => s === 'vc_fallback');
  const vcUsed = vcDone || vcCur || vcFallback;
  const intentDone = has((s) => isIntentLog(s));
  const intentLog = logs.find((l) => isIntentLog(l.stage));
  // 会话是否已收尾（AI 段结束）：已结束仍"未启动"的段=实际未参与，不标"待执行"画饼
  const transferred = has((s) => s.includes('transfer'));
  const ended = has((s) => s.includes('hangup') || s === 'survey_request');

  const latItems = Object.values(latency).flat();
  const llmLat = latItems.find((v) => v.label === 'LLM 响应');
  const ttsLat = latItems.find((v) => v.label === 'TTS 首帧') || latItems.find((v) => v.label === 'TTS 总耗');

  // 变声段：仅历史通话的日志里出现过 vc_* 才渲染；没出现直接不出现
  const vcStage: PipeStage | null = vcUsed
    ? {
        key: 'vc',
        label: '变声',
        icon: <IconSettings />,
        color: WARNING,
        status: vcCur ? 'current' : vcDone || vcFallback ? 'done' : 'pending',
        dur: vcCur
          ? '进行中…'
          : vcDone
            ? vcFallback
              ? '已连接·部分降级'
              : '已连接'
            : vcFallback
              ? '降级直放'
              : '待执行',
      }
    : null;

  const stages: PipeStage[] = [
    {
      key: 'asr',
      label: 'ASR 识别',
      icon: <IconSound />,
      color: PRIMARY,
      status: asrCount > 0 ? 'done' : 'none',
      dur: asrCount > 0 ? `×${asrCount} 轮` : '—',
    },
    {
      key: 'intent',
      label: '意图',
      icon: <IconMindMapping />,
      color: PURPLE,
      status: intentDone ? 'done' : 'none',
      dur: intentDone ? `${(offsetOf((s) => isIntentLog(s)) / 1000).toFixed(1)}s` : '—',
      extra: intentDone ? <AiBadge size="sm">{intentDisplay(intentLog as AICallLog)}</AiBadge> : undefined,
    },
    {
      key: 'llm',
      label: 'LLM 生成',
      icon: <IconRobot />,
      color: PURPLE,
      status: llmCur ? 'current' : llmDone ? 'done' : ended ? 'none' : 'pending',
      dur: llmLat ? `${(llmLat.ms / 1000).toFixed(1)}s` : '—',
    },
    {
      key: 'tts',
      label: 'TTS 播报',
      icon: <IconVoice />,
      color: CYAN,
      status: ttsCur ? 'current' : ttsDone ? 'done' : ended ? 'none' : 'pending',
      dur: ttsLat ? `${ttsLat.label === 'TTS 总耗' ? '总耗' : '首帧'} ${(ttsLat.ms / 1000).toFixed(1)}s` : '—',
    },
    ...(vcStage ? [vcStage] : []),
  ];

  // 后续阶段推进（进行中段之前的已完成，之后的待执行）——已按推导顺序，再兜底校准一次
  let currentIdx = -1;
  for (let i = 0; i < stages.length; i += 1) {
    if (stages[i].status === 'current') {
      currentIdx = i;
      break;
    }
  }
  if (currentIdx >= 0) {
    for (let i = currentIdx + 1; i < stages.length; i += 1) {
      if (stages[i].status === 'none') stages[i].status = 'pending';
    }
  }

  return (
    <div className="bcc-pipeline" style={{ border: '1px solid rgba(var(--bcc-ai-rgb), 0.3)', borderRadius: 12, padding: '14px 12px', background: 'rgba(var(--bcc-ai-rgb), 0.04)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <AiBadge size="sm">AI 处理流水线</AiBadge>
        <span style={{ fontSize: 11, color: 'var(--bcc-text-3)' }}>由 AI 调用日志实时推导</span>
        <span style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
          {transferred && <span className="pill-tiny pill-tiny-danger">已转人工</span>}
          {ended && !transferred && <span className="pill-tiny pill-tiny-ok">已结束</span>}
        </span>
      </div>
      <div className="bcc-pipeline-track">
        {stages.map((s, i) => (
          <div key={s.key} style={{ display: 'flex', alignItems: 'flex-start', flex: 1, minWidth: 0 }}>
            {i > 0 && (
              <div className={`bcc-pipe-conn${stages[i - 1].status === 'done' ? ' done' : ''}`} />
            )}
            <div className="bcc-pipe-stage">
              <div
                className={`bcc-pipe-dot ${s.status} ${s.status === 'current' ? 'bcc-pipe-current' : ''}`}
                style={{ background: s.color }}
              >
                {s.icon}
              </div>
              <div className="bcc-pipe-name" style={{ color: s.status === 'none' ? 'var(--bcc-text-3)' : undefined }}>{s.label}</div>
              <div className="bcc-pipe-dur">{s.dur}</div>
              {s.extra}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
